import { useParams } from 'react-router';
import { MoreVertical, CircleOff } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@predoc/cow';
import {
  useGetPatientRequests,
  useGetPatientById,
} from '../../../../hooks/patientsAPI';
import { OPEN_STATUSES } from '../../../../constants';
import { CancelOpenRequestsDialog, RequestStatusIcons } from '../../../common';
import { PatientsListRequestSection } from '../../../patient-list/PatientsListRequestSection';
import { TableSkeleton } from '@/components';
import type {
  PatientStatus,
  PatientRequest,
  ExistingPatient,
} from '../../../../types';

export const RequestsSection = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const { data: patientData, isLoading: isPatientLoading } =
    useGetPatientById(patientId);
  const { data, isLoading: isRequestsLoading } =
    useGetPatientRequests(patientId);

  const isLoading = isPatientLoading || isRequestsLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-full animate-pulse rounded bg-gray-100" />
        <TableSkeleton rows={5} columns={4} />
      </div>
    );
  }

  const organizationStatus = (data?.organizationStatus ?? {}) as Partial<
    Record<PatientStatus, number>
  >;
  const requests = data?.requests ?? [];
  const patient = patientData as ExistingPatient;
  const hasOpenRequests = OPEN_STATUSES.some(
    (status) => (organizationStatus[status] ?? 0) > 0,
  );

  if (requests.length === 0) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center px-4 py-12">
        <img
          src="/suggested-providers.png"
          alt="No requests to show"
          className="mb-4 max-w-[200px]"
        />
        <h3 className="text-lg font-semibold">No requests to show</h3>
        <p className="text-muted-foreground text-sm">
          Patient requests will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="bg-accent flex items-center justify-between rounded-lg border p-2">
        <RequestStatusIcons statusCounts={organizationStatus} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Request actions">
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            <CancelOpenRequestsDialog patientId={patientId!}>
              <DropdownMenuItem
                className="text-destructive"
                disabled={!hasOpenRequests}
                onSelect={(e: Event) => e.preventDefault()}
              >
                <CircleOff className="text-destructive size-4" />
                Cancel Open Requests
              </DropdownMenuItem>
            </CancelOpenRequestsDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Request Sections */}
      <div className="space-y-4">
        {requests.map((request, index) => (
          <PatientsListRequestSection
            key={request.crrId ?? index}
            request={request as PatientRequest}
            patient={patient}
            showCancelButton={false}
            showEditButton={false}
          />
        ))}
      </div>
    </div>
  );
};
